/** @jsxImportSource @emotion/react */

import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { Modal, Button, Typography, IconButton } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { findAllQuestionCategory_findAllQuestionCategory_items as QuestionCategory } from '__generatedTypes__/findAllQuestionCategory'
import { PlatformSlug } from 'constants/consts'
import Table, { FooterWrapper, HeaderWrapper, PaginationPage, PaginationTake } from 'components/Table'
import { Search } from 'components/Input'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { useQuestionHelpers } from 'pagesNew/Questions/Form/useQuestionHelpers'
import QuestionCategoryModal from './QuestionCategoryModal'
import { useQuestionCategories } from './useQuestionCategories'
import * as classes from './styles'

const QuestionCategories: FC = () => {
  const { platform } = useParams()
  const currentPlatformSlug = platform ? (String(platform) as PlatformSlug) : undefined
  const { items, refetch, pagination, search, order, loaded } = useQuestionCategories()
  const {
    allSubjectGroups,
    allPlatforms,
    getAvailableSubjectGroups,
    loading: loadingSubjectGroups,
  } = useQuestionHelpers()

  useEffect(() => {
    refetch()
  }, [refetch])

  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState<QuestionCategory | null>(null)
  const [isModalCreateOpen, setIsModalCreateOpen] = useState<boolean>(false)

  const handleCloseModal = () => {
    setSelectedQuestionCategory(null)
    setIsModalCreateOpen(false)
  }

  return (
    <div css={classes.container}>
      <div css={classes.headerContainer}>
        <Typography css={classes.title} variant="title3">
          Edit Question Categories
        </Typography>
        <Button size="medium" color="blue" onClick={() => setIsModalCreateOpen(true)}>
          <AddRoundedIcon css={classes.plusIcon} />
          New Category
        </Button>
      </div>

      <HeaderWrapper css={classes.headerWrapper}>
        <Search {...search} css={classes.search} />
        <PaginationPage {...pagination} />
      </HeaderWrapper>
      <Table
        columns={[
          {
            title: 'Category Title',
            renderCell: (questionCategory) => questionCategory.title,
            flex: 2,
          },
          {
            title: 'Subject',
            renderCell: (questionCategory) =>
              !questionCategory.subjects?.length
                ? '-'
                : allSubjectGroups.find((subjectGroup) =>
                    questionCategory.subjects?.every((subject) =>
                      subjectGroup.subjects.some((s) => s.id === subject.id)
                    )
                  )?.title ?? '-',
            flex: 2,
          },
          {
            title: 'Date Added',
            renderCell: (questionCategory) => format(new Date(questionCategory.createdAt), 'd LLLL, yyyy '),
            orderField: 'createdAt',
          },
          {
            renderCell: (questionCategory) => (
              <IconButton size="small" onClick={() => setSelectedQuestionCategory(questionCategory)}>
                <EditIcon />
              </IconButton>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={items}
        order={order.value}
        onOrder={order.onChange}
        wrapperMinWidth={500}
        isNotLoaded={!loaded || loadingSubjectGroups}
      />
      <FooterWrapper>
        <PaginationTake {...pagination} />
      </FooterWrapper>

      <Modal open={isModalCreateOpen || !!selectedQuestionCategory} onClose={handleCloseModal}>
        <div>
          <QuestionCategoryModal
            questionCategory={selectedQuestionCategory}
            allSubjectGroups={allSubjectGroups}
            allPlatforms={allPlatforms}
            getAvailableSubjectGroups={getAvailableSubjectGroups}
            currentPlatformSlug={currentPlatformSlug}
            handleClose={() => {
              handleCloseModal()
              refetch()
            }}
          />
        </div>
      </Modal>
    </div>
  )
}

export default QuestionCategories
